/**
 * config/mail.ts — fallback mail hostnames for Business Email v2 (mx1).
 *
 * These are ONLY used when the API response doesn't carry the hostnames
 * (older payloads, network hiccup mid-render). Every surface that shows
 * IMAP/SMTP/POP3/webmail settings must prefer the values from
 * GET /api/email/setup/v2/:id (imap_host, smtp_host, pop3_host, *_port,
 * webmail.url) and fall back to these.
 */
export const MAIL_HOSTS = {
  imap: 'mx1.12and22.com',
  smtp: 'mx1.12and22.com',
  pop3: 'mx1.12and22.com',
  webmail: 'https://inbox.12and22.com',
} as const;

export const MAIL_PORTS = {
  imap: 993,
  smtp: 587,
  pop3: 995,
} as const;

/** Shape of the optional connection fields on the v2 setup payload. */
export interface MailConnectionFields {
  imap_host?: string | null;
  smtp_host?: string | null;
  pop3_host?: string | null;
  imap_port?: number | null;
  smtp_port?: number | null;
  pop3_port?: number | null;
  webmail?: { url?: string | null; shared?: boolean } | null;
}

/**
 * Resolve the connection settings to display: API values first, MAIL_HOSTS
 * as the fallback. Safe to call with `undefined`/`null`.
 */
export function resolveMailSettings(src?: MailConnectionFields | null) {
  return {
    imapHost: src?.imap_host || MAIL_HOSTS.imap,
    smtpHost: src?.smtp_host || MAIL_HOSTS.smtp,
    pop3Host: src?.pop3_host || MAIL_HOSTS.pop3,
    imapPort: src?.imap_port || MAIL_PORTS.imap,
    smtpPort: src?.smtp_port || MAIL_PORTS.smtp,
    pop3Port: src?.pop3_port || MAIL_PORTS.pop3,
    webmailUrl: src?.webmail?.url || MAIL_HOSTS.webmail,
  };
}

// ---------------------------------------------------------------------------
// Business Email v2 — connection-settings payload
// (GET /api/email/setup/v2/domains/:id/connection-settings)

/** Fallback sending/receiving limits shown on the Connect page. */
export const MAIL_LIMITS = {
  outbound_per_hour: 500,
  max_message_mb: 50,
  /** Attachments are base64-encoded in transit, so the practical cap is
   *  lower than the message size. */
  attachment_mb: 35,
} as const;

export interface AutoconfigStatus {
  autoconfig: boolean;
  autodiscover: boolean;
  srv: boolean;
}

export interface ConnectionSettings {
  imap: { host: string; port: number; security: string };
  smtp: { host: string; port: number; security: string; auth: string; username: string };
  pop3: { host: string; port: number; security: string };
  webmail_url: string;
  send_as_aliases: boolean;
  autoconfig: {
    thunderbird_url?: string | null;
    autodiscover_srv?: string | null;
    status: AutoconfigStatus;
  };
  limits: { outbound_per_hour: number; max_message_mb: number; attachment_mb: number };
}

/** Deep-partial shape we accept from the wire before resolving. */
export type ConnectionSettingsPayload = {
  imap?: Partial<ConnectionSettings['imap']> | null;
  smtp?: Partial<ConnectionSettings['smtp']> | null;
  pop3?: Partial<ConnectionSettings['pop3']> | null;
  webmail_url?: string | null;
  send_as_aliases?: boolean | null;
  autoconfig?: {
    thunderbird_url?: string | null;
    autodiscover_srv?: string | null;
    status?: Partial<AutoconfigStatus> | null;
  } | null;
  limits?: Partial<ConnectionSettings['limits']> | null;
};

const port = (v: unknown, fallback: number) => {
  const n = typeof v === 'number' ? v : parseInt(String(v ?? ''), 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

/**
 * Resolve the full connection-settings object: API values first, the
 * MAIL_HOSTS / MAIL_PORTS / MAIL_LIMITS constants as the fallback. Safe to
 * call with `undefined`/`null` (e.g. while the request is in flight).
 */
export function resolveConnectionSettings(src?: ConnectionSettingsPayload | null): ConnectionSettings {
  const status = src?.autoconfig?.status;
  return {
    imap: {
      host: src?.imap?.host || MAIL_HOSTS.imap,
      port: port(src?.imap?.port, MAIL_PORTS.imap),
      security: src?.imap?.security || 'SSL/TLS',
    },
    smtp: {
      host: src?.smtp?.host || MAIL_HOSTS.smtp,
      port: port(src?.smtp?.port, MAIL_PORTS.smtp),
      security: src?.smtp?.security || 'STARTTLS',
      auth: src?.smtp?.auth || 'Normal password',
      username: src?.smtp?.username || '',
    },
    pop3: {
      host: src?.pop3?.host || MAIL_HOSTS.pop3,
      port: port(src?.pop3?.port, MAIL_PORTS.pop3),
      security: src?.pop3?.security || 'SSL/TLS',
    },
    webmail_url: src?.webmail_url || MAIL_HOSTS.webmail,
    send_as_aliases: src?.send_as_aliases !== false,
    autoconfig: {
      thunderbird_url: src?.autoconfig?.thunderbird_url ?? null,
      autodiscover_srv: src?.autoconfig?.autodiscover_srv ?? null,
      status: {
        autoconfig: !!status?.autoconfig,
        autodiscover: !!status?.autodiscover,
        srv: !!status?.srv,
      },
    },
    limits: {
      outbound_per_hour: port(src?.limits?.outbound_per_hour, MAIL_LIMITS.outbound_per_hour),
      max_message_mb: port(src?.limits?.max_message_mb, MAIL_LIMITS.max_message_mb),
      attachment_mb: port(src?.limits?.attachment_mb, MAIL_LIMITS.attachment_mb),
    },
  };
}

/**
 * Normalise a security label for display ("ssl", "tls", "SSL/TLS" → "SSL/TLS";
 * "starttls" → "STARTTLS"; anything else passes through).
 */
export function securityLabel(s: string | null | undefined): string {
  const v = String(s || '').trim().toLowerCase().replace(/[\s_-]/g, '');
  if (!v) return 'SSL/TLS';
  if (v === 'starttls') return 'STARTTLS';
  if (v === 'ssl' || v === 'ssltls' || v === 'tls' || v === 'implicit' || v === 'implicittls') return 'SSL/TLS';
  if (v === 'none' || v === 'plain') return 'None';
  return String(s);
}
